
import { Auth } from "@/components/Auth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import type { User } from "@supabase/supabase-js";
import { UserRound } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useCredits } from "@/hooks/useCredits";
import { useState } from "react";
import { getCheckoutUrl } from "@/utils/stripe-utils";
import { Link } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

interface HeaderProps {
  user: User | null;
}

export const Header = ({ user }: HeaderProps) => {
  const { toast } = useToast();
  const avatarUrl = user?.user_metadata?.avatar_url;
  const { credits, isLoading: isLoadingCredits } = useCredits(user);
  const [isLoading, setIsLoading] = useState(false);
  const userName = user?.user_metadata?.full_name || user?.email || 'User';

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      window.location.href = 'https://makesign.ai';
    } catch (error) {
      console.error('[Header] Error during sign out:', error);
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleTopUpClick = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "Please log in to purchase credits",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsLoading(true);
      console.log('[Header] Starting checkout process for user:', {
        userId: user.id,
        timestamp: new Date().toISOString()
      });

      const url = await getCheckoutUrl(user.id);
      if (!url) {
        throw new Error('No checkout URL received');
      }

      console.log('[Header] Redirecting to checkout URL:', {
        userId: user.id,
        hasUrl: !!url,
        timestamp: new Date().toISOString()
      });
      
      window.location.href = url;
    } catch (error) {
      console.error('[Header] Error during checkout:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        userId: user.id,
        timestamp: new Date().toISOString()
      });
      
      toast({
        title: "Checkout Error",
        description: error instanceof Error ? error.message : "Failed to start checkout process. Please try again.",
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full h-20 bg-transparent">
      <div className="h-full mx-auto px-4 bg-transparent">
        <div className="flex justify-between items-center h-full">
          <Link to="/" className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity">
            <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-10 md:h-10">
              <path d="M37 33C25.6339 30.3333 14.3661 30.3333 3 33M3.95834 20.6778L8.64544 25.3647M8.64526 20.6778L3.95816 25.3647M13.45 37.0238C19.7125 27.544 29.1132 6.33269 23.0127 3.42699C18.9002 1.46815 14.0596 6.18172 13.45 11.9731C12.8403 17.7645 14.9546 22.6 21.6588 23.1436C28.3631 23.6872 30.6818 19.2819 31.5877 19.2819C32.4939 19.2819 32.4033 24.8083 37.0238 24.8083" stroke="#FF2F00" strokeWidth="2" strokeLinecap="square" strokeLinejoin="round" />
            </svg>
            <span className="text-lg md:text-xl">Make a Sign</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            {user && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-4">
                  <div className="text-base md:text-[20px] text-black flex items-center gap-2">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-6 md:h-6">
                      <path d="M10.101 4C11.3636 2.76281 13.0927 2 15 2C18.866 2 22 5.13401 22 9C22 10.9073 21.2372 12.6365 19.9999 13.899M16 15C16 18.866 12.866 22 9 22C5.13401 22 2 18.866 2 15C2 11.134 5.13401 8 9 8C12.866 8 16 11.134 16 15Z" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {isLoadingCredits ? "..." : credits}
                  </div>
                  <button 
                    className="text-[#FF2F00] text-sm md:text-base hover:opacity-80 transition-opacity px-2 md:px-3 py-1 rounded-full border border-[#FF2F00]"
                    onClick={handleTopUpClick}
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading..." : "Get more"}
                  </button>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 md:p-2 rounded-full hover:bg-gray-100 transition-colors">
                      <Avatar className="w-7 h-7 md:w-8 md:h-8">
                        <AvatarImage src={avatarUrl} alt="User avatar" />
                        <AvatarFallback>
                          <UserRound className="w-4 h-4 md:w-6 md:h-6 text-gray-600" />
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>{userName}</DropdownMenuLabel>
                    <DropdownMenuItem onClick={handleSignOut}>
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
            {!user && <Auth />}
          </div>
        </div>
      </div>
    </div>
  );
};
